/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MealRemindersPatchRequest } from '../models/MealRemindersPatchRequest';
import type { SuccessMealRemindersResponse } from '../models/SuccessMealRemindersResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MealReminderControllerService {

    /**
     * Returns the member's meal reminders.
     * Before the member's first save these are the defaults on offer (keys breakfast, lunch, snack, dinner), with the toggle off. status and activeThrough are computed for today in the member's timezone; a round that is over reads as ENDED. calibrationCompleted and everEnabled are derived on every read too (see their schema). Nothing is stored by reading.
     * @returns SuccessMealRemindersResponse The member's meal reminders.
     * @throws ApiError
     */
    public static getMealReminders(): CancelablePromise<SuccessMealRemindersResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v1/notifications/meal-reminders',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Resets the member's meal reminders to the defaults.
     * QA tool: only available where the MEAL_REMINDER_RESET feature is on, never in production. Forgets what the member saved, including the round, so they are offered the defaults again with the toggle off (status OFF) and everEnabled false, as before their first save. calibrationCompleted is unaffected: it follows the member's meals. Repeating it is safe.
     * @returns SuccessMealRemindersResponse The defaults, which is what a GET returns next.
     * @throws ApiError
     */
    public static resetMealReminders(): CancelablePromise<SuccessMealRemindersResponse> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/v1/notifications/meal-reminders',
            errors: {
                400: `Bad Request`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `MEAL_REMINDER_RESET is off. Nothing was reset.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

    /**
     * Changes the member's meal reminders.
     * Lists only what changes; every reminder and field left out stays as it is.
     * - Each entry names one reminder by its key, which the app chooses and the backend never interprets; name each key at most once.
     * - time changes a reminder's time in place. remove: true deletes it.
     * - A key that isn't stored adds a reminder; it needs a time, and is on and every day unless given.
     * - Two reminders may share a time only on disjoint days.
     * - Entries apply all at once, against the reminders as stored, so their order does not matter: two reminders can swap times in one request.
     * - Repeating a request is safe: deleting a key that is gone does nothing.
     * - A body that changes nothing ({} or "reminders": []) saves nothing, not even a first save.
     *
     * Rounds: reminders run for 7 member-local days at a time. enabled: true starts a round when the toggle was off (or nothing was saved yet), and starts a new one when the last has ENDED. Edits, per-reminder toggles and enabled: true during a running round leave activeThrough as it is. enabled: false keeps the reminders.
     *
     * Examples:
     * - First save, defaults as offered: {"enabled": true} → ACTIVE, activeThrough = today + 6
     * - Edit during a round: {"reminders": [{"key": "lunch", "time": "13:00"}, {"key": "snack", "enabled": true}]} → same activeThrough
     * - Weekday/weekend split: {"reminders": [{"key": "breakfast", "time": "08:00", "days": ["MON","TUE","WED","THU","FRI"]}, {"key": "breakfast-weekend", "time": "09:00", "days": ["SAT","SUN"]}]}
     * - Merge back: {"reminders": [{"key": "breakfast", "days": ["MON","TUE","WED","THU","FRI","SAT","SUN"]}, {"key": "breakfast-weekend", "remove": true}]}
     * - Turn off: {"enabled": false} → OFF, activeThrough null, reminders kept, everEnabled stays true
     * - Renew: {"enabled": true} while ENDED → ACTIVE, new activeThrough
     *
     * Returns the full state, which is what a GET returns next.
     * @param requestBody
     * @returns SuccessMealRemindersResponse The meal reminders after the change.
     * @throws ApiError
     */
    public static patchMealReminders(
        requestBody: MealRemindersPatchRequest,
    ): CancelablePromise<SuccessMealRemindersResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/v1/notifications/meal-reminders',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `A malformed entry (blank or over-long key, unknown day), a key listed twice, a new key without a time, more than 16 entries, or a result with two reminders at the same time on a shared day, a reminder without days or more than 8 reminders. Nothing was saved.`,
                401: `The client is not authorized to perform the action. The \`code\`, \`message\`, and \`data\` fields may provide more information about the reason of the error.`,
                404: `The requested entity was not found, or the client has no permission to access the entity.`,
                500: `An unexpected error has occurred.`,
            },
        });
    }

}
